/**
 * Account Context - Provides account data to all child components
 * 
 * When unauthenticated, provides mock data for preview mode.
 * When authenticated, provides real data from the backend.
 */

import { createContext, useContext, type ReactNode } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { usePortfolioData } from "./hooks/use-portfolio-data";
import { getMockPortfolioData } from "@/lib/mock-data";
import type { DateRangeValue } from "@/components/ui/date-range-selector";

type AccountContextValue = ReturnType<typeof usePortfolioData>;

const AccountContext = createContext<AccountContextValue | null>(null);

export const useAccountContext = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error("useAccountContext must be used within AccountProvider");
  }
  return context;
};

type AccountProviderProps = {
  children: ReactNode;
  dateRange: DateRangeValue;
};

export const AccountProvider = ({ children, dateRange }: AccountProviderProps) => {
  const { isAuthenticated } = useUnifiedAuth();
  const realData = usePortfolioData({ dateRange });
  
  // Use mock data for preview when not authenticated
  const mockData = getMockPortfolioData();
  
  const value: AccountContextValue = isAuthenticated 
    ? realData 
    : {
        userId: null,
        accountSummary: mockData.accountSummary,
        positions: mockData.positions,
        fills: mockData.fills,
        deposits: mockData.deposits,
        withdrawals: mockData.withdrawals,
        fundingPayments: mockData.fundingPayments,
        orders: mockData.orders,
        isLoading: false,
      };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};
